import React from "react";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import { FormControl, FormHelperText, FormLabel } from "@mui/material";
import { FieldValidator } from "final-form";

import { JsonEditor } from "ui";

export type JsonFieldValue = string | null;
export type JsonFieldProps = {
  name: string;
  label?: string;
  required?: boolean;
  hideError?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  height?: string;
  helperText?: string;
  fieldProps?: Partial<FieldProps<JsonFieldValue, JsonWrapperProps, HTMLElement, string>>;
  validate?: FieldValidator<JsonFieldValue>;
  defaultValue?: string;
};

const JsonField: React.FC<JsonFieldProps> = (props: JsonFieldProps) => {
  const { name, hideError, fieldProps, validate, defaultValue, ...rest } = props;

  return (
    <Field
      name={name}
      render={({ input, meta }) => <JsonFieldWrapper input={input} meta={meta} name={name} hideError={hideError} {...rest} />}
      validate={validate}
      defaultValue={defaultValue}
      validateFields={[]}
      {...fieldProps}
    />
  );
};

type JsonWrapperProps = Pick<JsonFieldProps, "hideError" | "height" | "disabled" | "label" | "required" | "fullWidth" | "className" | "helperText"> &
  FieldRenderProps<string, HTMLElement>;

const JsonFieldWrapper: React.FC<JsonWrapperProps> = ({
  input: { name, onChange, value, onFocus, onBlur, ...restInput },
  meta,
  disabled,
  label,
  required,
  fullWidth = true,
  className,
  hideError = false,
  height = "200px",
  helperText,
}: JsonWrapperProps) => {
  const hasError = Boolean((meta.error || meta.submitError) && meta.touched);

  return (
    <FormControl fullWidth={fullWidth} data-testid={`${name}-json-field`} className={className}>
      {(label || required) && (
        <FormLabel required={required} error={hasError} htmlFor={name} sx={{ marginBottom: 1 }}>
          {label}
        </FormLabel>
      )}
      <JsonEditor
        value={value || ""}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        height={height}
        disabled={disabled}
        hasError={hasError && !hideError}
        testId={`${name}-json-editor`}
      />
      {((hasError && !hideError) || helperText) && (
        <FormHelperText error={hasError && !hideError} data-testid={`${name}-helper-text`}>
          {(!hasError || hideError) && helperText ? helperText : ""}
          {meta.touched && hasError && !hideError && (meta.error || meta.submitError)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default JsonField;
